import { useState } from 'react';
import {
  Check,
  Mail,
  Edit3,
  Copy,
  Info,
  HelpCircle,
  MoreVertical,
  Download,
  Trash2,
  Home,
  Settings,
  FileText,
  Monitor,
  Tablet,
  Smartphone,
} from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Card } from '../../../components/ui/Card';
import { Modal } from '../../../components/ui/Modal';
import { Dropdown } from '../../../components/ui/Dropdown';
import { Tooltip } from '../../../components/ui/Tooltip';
import { Popover } from '../../../components/ui/Popover';
import { ContextMenu } from '../../../components/ui/ContextMenu';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../components/ui/Tabs';
import { Breadcrumbs } from '../../../components/ui/Breadcrumbs';
import { Pagination } from '../../../components/ui/Pagination';
import { SegmentedTabs } from '../../../components/ui/SegmentedTabs';
import { FormField } from '../../../components/ui/FormField';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { Textarea } from '../../../components/ui/Textarea';
import { CodeSnippet } from '../components/CodeSnippet';
import { Divider } from '../../../components/ui/Divider';
import { Subsection } from '../components/Subsection';

// ============================================================================
// Sekcja: Modal
// ============================================================================

export function ModalSection() {
  const [basic, setBasic] = useState(false);
  const [withForm, setWithForm] = useState(false);
  const [sm, setSm] = useState(false);
  const [xl, setXl] = useState(false);

  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Modal>'}</code>{' '}
        - overlay renderowany przez <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">createPortal</code>{' '}
        do <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">document.body</code>.
        Backdrop click + Escape zamykają (domyślnie). Locks body scroll gdy otwarty.
        Dla potwierdzeń OK/Cancel użyj istniejącego <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">ConfirmDialog</code>.
      </p>

      <CodeSnippet
        language="tsx"
        code={`import { Modal } from '@/components/ui/Modal';

const [open, setOpen] = useState(false);

<Modal
  isOpen={open}
  onClose={() => setOpen(false)}
  title="Dodaj klienta"
  description="Podstawowe dane"
  footer={
    <div className="flex items-center justify-between gap-2">
      <Button variant="primary" leftIcon={<Check />}>Zapisz</Button>
      <Button variant="ghost" onClick={() => setOpen(false)}>Anuluj</Button>
    </div>
  }
>
  {/* form content */}
</Modal>`}
      />

      <Subsection n={1} title="Rozmiary">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4">
          <div className="flex flex-wrap gap-3">
            <Button variant="primary" onClick={() => setBasic(true)}>Otwórz modal (md)</Button>
            <Button variant="secondary" onClick={() => setWithForm(true)}>Modal z formularzem</Button>
            <Button variant="ghost" onClick={() => setSm(true)}>Mały (sm)</Button>
            <Button variant="ghost" onClick={() => setXl(true)}>Duży (xl)</Button>
          </div>
        </div>
      </Subsection>

      <Modal isOpen={basic} onClose={() => setBasic(false)} title="Podstawowy modal" description="To jest standardowy modal z tytułem, opisem i footer.">
        <p className="text-sm text-text-secondary leading-relaxed">
          Treść modala. Możesz wrzucić dowolny JSX. Backdrop click albo Escape zamykają.
          Body scroll zablokowany. Render przez portal omija z-index w rodzicach.
        </p>
      </Modal>

      <Modal
        isOpen={withForm}
        onClose={() => setWithForm(false)}
        title="Dodaj klienta"
        description="Podstawowe dane - resztę uzupełnisz potem."
        footer={
          <div className="flex items-center justify-between gap-2">
            <Button variant="primary" leftIcon={<Check />}>Zapisz</Button>
            <Button variant="ghost" onClick={() => setWithForm(false)}>Anuluj</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <FormField label="Imię i nazwisko" htmlFor="m-name" required>
            <Input id="m-name" placeholder="Jan Kowalski" />
          </FormField>
          <FormField label="Email" htmlFor="m-email" required>
            <Input id="m-email" type="email" leftIcon={<Mail />} placeholder="jan@example.com" />
          </FormField>
          <FormField label="Notatka" htmlFor="m-note" helper="Opcjonalna">
            <Textarea id="m-note" rows={3} placeholder="Kontekst klienta..." />
          </FormField>
        </div>
      </Modal>

      <Modal isOpen={sm} onClose={() => setSm(false)} size="sm" title="Mały modal">
        <p className="text-sm text-text-secondary">Max-w-sm. Dla prostych komunikatów lub mini-formularzy.</p>
      </Modal>

      <Modal isOpen={xl} onClose={() => setXl(false)} size="xl" title="Duży modal (xl)" description="Max-w-2xl - dla tabel, preview, edytorów">
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Tutaj można wrzucić tabelę albo bardziej rozbudowaną zawartość. Content area ma overflow-y-auto,
            więc długa treść scrolluje wewnątrz (max-height 90vh na całym modalu).
          </p>
          <div className="grid grid-cols-3 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} padding="sm">
                <div className="text-xs text-text-muted">Karta {i + 1}</div>
                <div className="text-xl font-bold text-text-primary">{((i + 1) * 142).toLocaleString()}</div>
              </Card>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ============================================================================
// Sekcja: Tooltip
// ============================================================================

export function TooltipSection() {
  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Tooltip>'}</code>{' '}
        - hover tooltip z delay (default 300ms). Wrapuje jedno dziecko, klonuje je
        z <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">onMouseEnter/Leave</code>{' '}
        i aria-describedby. Side: top / right / bottom / left (bez collision detection).
      </p>

      <Subsection n={1} title="Strony">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-10 flex flex-wrap items-center justify-center gap-8">
          <Tooltip content="Tooltip na górze" side="top">
            <Button variant="secondary" size="sm">top</Button>
          </Tooltip>
          <Tooltip content="Tooltip z prawej" side="right">
            <Button variant="secondary" size="sm">right</Button>
          </Tooltip>
          <Tooltip content="Tooltip na dole" side="bottom">
            <Button variant="secondary" size="sm">bottom</Button>
          </Tooltip>
          <Tooltip content="Tooltip z lewej" side="left">
            <Button variant="secondary" size="sm">left</Button>
          </Tooltip>
        </div>
      </Subsection>

      <Subsection n={2} title="Przykłady biznesowe">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-6 flex items-center gap-6">
          <Tooltip content="Pokaż pomoc (Shift+?)" side="bottom">
            <Button variant="icon" size="sm" leftIcon={<HelpCircle />} aria-label="Pomoc" />
          </Tooltip>
          <Tooltip content="Edytuj" side="top">
            <Button variant="icon" size="sm" leftIcon={<Edit3 />} aria-label="Edytuj" />
          </Tooltip>
          <Tooltip content="Skopiuj do schowka" side="top">
            <Button variant="icon" size="sm" leftIcon={<Copy />} aria-label="Kopiuj" />
          </Tooltip>
          <Tooltip content="Więcej informacji o tym polu" side="right" delay={100}>
            <Info className="w-4 h-4 text-text-muted cursor-help" />
          </Tooltip>
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: Dropdown + Popover + ContextMenu
// ============================================================================

export function DropdownSection() {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const dropdownItems = [
    { id: 'edit',   label: 'Edytuj',       icon: <Edit3 /> },
    { id: 'copy',   label: 'Duplikuj',     icon: <Copy /> },
    { id: 'export', label: 'Eksportuj',    icon: <Download /> },
    { id: 'delete', label: 'Usuń',         icon: <Trash2 />, destructive: true, divider: true },
  ];

  const ctxItems = [
    { id: 'open', label: 'Otwórz', icon: <Info /> },
    { id: 'edit', label: 'Edytuj', icon: <Edit3 /> },
    { id: 'copy', label: 'Skopiuj ID', icon: <Copy /> },
    { id: 'del',  label: 'Usuń', icon: <Trash2 />, destructive: true, divider: true },
  ];

  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Trzy komponenty w jednej sekcji: <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Dropdown>'}</code>{' '}
        (menu z listą akcji), <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Popover>'}</code>{' '}
        (dowolna zawartość), <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<ContextMenu>'}</code>{' '}
        (right-click).
      </p>

      <Subsection n={1} title="Dropdown (klik)">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 flex flex-wrap gap-3 items-center">
          <Dropdown
            trigger={<Button variant="secondary" size="sm" rightIcon={<MoreVertical />}>Akcje</Button>}
            items={dropdownItems}
          />
          <Dropdown
            align="end"
            trigger={<Button variant="icon" size="sm" leftIcon={<MoreVertical />} aria-label="Menu" />}
            items={dropdownItems}
          />
          <span className="text-xs text-text-muted">Keyboard: ↑↓ Enter Esc</span>
        </div>
      </Subsection>

      <Subsection n={2} title="Popover (dowolna zawartość)">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 flex flex-wrap gap-3 items-center">
          <Popover
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
            trigger={<Button variant="secondary" size="sm">Filtry</Button>}
            content={
              <div className="p-4 w-64 space-y-3">
                <div className="text-sm font-semibold text-text-primary">Filtruj zamówienia</div>
                <FormField label="Status" htmlFor="pop-status">
                  <Select id="pop-status" defaultValue="">
                    <option value="">Wszystkie</option>
                    <option value="new">Nowe</option>
                    <option value="shipped">Wysłane</option>
                  </Select>
                </FormField>
                <FormField label="Data od" htmlFor="pop-date">
                  <Input id="pop-date" type="date" />
                </FormField>
                <div className="flex justify-end pt-1">
                  <Button variant="primary" size="sm" onClick={() => setPopoverOpen(false)}>Zastosuj</Button>
                </div>
              </div>
            }
          />
          <Popover
            align="end"
            trigger={<Button variant="ghost" size="sm" leftIcon={<Info />}>Szczegóły</Button>}
            content={
              <div className="p-4 w-56">
                <div className="text-sm font-semibold text-text-primary mb-1">Status integracji</div>
                <p className="text-xs text-text-muted leading-relaxed">
                  Ostatni sync: 2 min temu. Kolejny zaplanowany o 09:30.
                </p>
                <Badge variant="success" size="sm" className="mt-2">Aktywny</Badge>
              </div>
            }
          />
        </div>
      </Subsection>

      <Subsection n={3} title="ContextMenu (right-click)">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4">
          <ContextMenu items={ctxItems}>
            <div className="border-2 border-dashed border-border rounded-xl p-10 text-center cursor-context-menu hover:border-accent-primary/30 transition-colors">
              <p className="text-sm text-text-secondary">Kliknij prawym przyciskiem myszy tutaj</p>
              <p className="text-xs text-text-muted mt-1">Menu pojawi się w pozycji kursora</p>
            </div>
          </ContextMenu>
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: Nawigacja (Tabs + Breadcrumbs + Pagination)
// ============================================================================

export function NavigationSection() {
  const [page, setPage] = useState(5);
  const [pageSize, setPageSize] = useState(20);

  return (
    <div className="space-y-10">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        Trzy komponenty nawigacji: <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Tabs>'}</code>{' '}
        (composable pattern z keyboard nav), <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Breadcrumbs>'}</code>{' '}
        (ścieżka z ChevronRight), <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Pagination>'}</code>{' '}
        (numery + prev/next + page size).
      </p>

      <Subsection n={1} title="Tabs (horizontal)">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4">
          <Tabs defaultValue="ogolne">
            <TabsList>
              <TabsTrigger value="ogolne" leftIcon={<Home />}>Ogólne</TabsTrigger>
              <TabsTrigger value="konto" leftIcon={<Settings />}>Konto</TabsTrigger>
              <TabsTrigger value="faktury" leftIcon={<FileText />}>Faktury</TabsTrigger>
              <TabsTrigger value="disabled" disabled>Wkrótce</TabsTrigger>
            </TabsList>
            <TabsContent value="ogolne">
              <div className="rounded-lg border border-border-subtle bg-surface-raised/40 p-4">
                <p className="text-sm text-text-secondary">Panel ogólny. Keyboard: ← → żeby zmienić tab, Home/End dla pierwszej/ostatniej.</p>
              </div>
            </TabsContent>
            <TabsContent value="konto">
              <div className="rounded-lg border border-border-subtle bg-surface-raised/40 p-4">
                <p className="text-sm text-text-secondary">Ustawienia konta użytkownika.</p>
              </div>
            </TabsContent>
            <TabsContent value="faktury">
              <div className="rounded-lg border border-border-subtle bg-surface-raised/40 p-4">
                <p className="text-sm text-text-secondary">Lista faktur z ostatnich 12 miesięcy.</p>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </Subsection>

      <Subsection n={2} title="Tabs (vertical)">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4">
          <Tabs defaultValue="a" orientation="vertical">
            <div className="flex gap-4">
              <TabsList className="shrink-0">
                <TabsTrigger value="a">Integracje</TabsTrigger>
                <TabsTrigger value="b">API klucze</TabsTrigger>
                <TabsTrigger value="c">Webhooks</TabsTrigger>
              </TabsList>
              <div className="flex-1">
                <TabsContent value="a" className="mt-0">
                  <div className="rounded-lg border border-border-subtle bg-surface-raised/40 p-4 h-full">
                    <p className="text-sm text-text-secondary">Panel integracji. Keyboard ↑↓ przełącza.</p>
                  </div>
                </TabsContent>
                <TabsContent value="b" className="mt-0">
                  <div className="rounded-lg border border-border-subtle bg-surface-raised/40 p-4 h-full">
                    <p className="text-sm text-text-secondary">API klucze - tworzenie i rotacja.</p>
                  </div>
                </TabsContent>
                <TabsContent value="c" className="mt-0">
                  <div className="rounded-lg border border-border-subtle bg-surface-raised/40 p-4 h-full">
                    <p className="text-sm text-text-secondary">Webhooks - wysyłamy eventy do Twojego serwera.</p>
                  </div>
                </TabsContent>
              </div>
            </div>
          </Tabs>
        </div>
      </Subsection>

      <Subsection n={3} title="Breadcrumbs">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 space-y-4">
          <Breadcrumbs
            items={[
              { label: 'Panel', href: '#', icon: <Home /> },
              { label: 'Zamówienia', href: '#' },
              { label: 'Zamówienie #12345' },
            ]}
          />
          <Breadcrumbs
            items={[
              { label: 'Kreator', href: '#' },
              { label: 'Lejki', href: '#' },
              { label: 'Czarny Piątek 2025', href: '#' },
              { label: 'Upsell #2' },
            ]}
          />
          <Breadcrumbs
            items={[
              { label: 'Settings', href: '#' },
              { label: 'Integrations' },
            ]}
          />
        </div>
      </Subsection>

      <Subsection n={4} title="Pagination">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-4 space-y-5">
          <div>
            <p className="text-xs text-text-muted mb-2">Kilka stron (bez ellipsis):</p>
            <Pagination page={2} pageCount={5} onPageChange={() => {}} />
          </div>
          <Divider />
          <div>
            <p className="text-xs text-text-muted mb-2">Dużo stron (z ellipsis, klikalny):</p>
            <Pagination
              page={page}
              pageCount={20}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[10, 20, 50, 100]}
            />
            <p className="text-[11px] text-text-disabled mt-2">Bieżąca strona: {page}, rozmiar: {pageSize}</p>
          </div>
          <Divider />
          <div>
            <p className="text-xs text-text-muted mb-2">Skrajne pozycje:</p>
            <div className="space-y-3">
              <Pagination page={1} pageCount={20} onPageChange={() => {}} />
              <Pagination page={20} pageCount={20} onPageChange={() => {}} />
            </div>
          </div>
        </div>
      </Subsection>
    </div>
  );
}

// ============================================================================
// Sekcja: SegmentedTabs
// ============================================================================

export function SegmentedSection() {
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [period, setPeriod] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  const [view, setView] = useState<'list' | 'grid'>('list');

  return (
    <div className="space-y-8">
      <p className="text-sm text-text-muted leading-relaxed max-w-3xl">
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<SegmentedTabs>'}</code>{' '}
        — pill-style mini tabs dla switcherów w ekranach (Desktop/Tablet/Mobile, quick filters, view modes).{' '}
        <strong>NIE używaj dla głównej nawigacji</strong> — tam jest{' '}
        <code className="px-1.5 py-0.5 rounded bg-surface-raised text-text-secondary text-[11px]">{'<Tabs>'}</code>.
      </p>

      <Subsection n={1} title="Z ikonami (breakpoint switcher)">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-5 space-y-4">
          <SegmentedTabs
            value={device}
            onChange={setDevice}
            options={[
              { value: 'desktop', label: 'Desktop', icon: Monitor },
              { value: 'tablet',  label: 'Tablet',  icon: Tablet },
              { value: 'mobile',  label: 'Mobile',  icon: Smartphone },
            ]}
          />
          <p className="text-xs text-text-muted">Aktywny: {device}</p>
        </div>
      </Subsection>

      <Subsection n={2} title="Bez ikon (period filter)">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-5 space-y-4">
          <SegmentedTabs
            value={period}
            onChange={setPeriod}
            size="sm"
            options={[
              { value: '7d',  label: '7 dni' },
              { value: '30d', label: '30 dni' },
              { value: '90d', label: '90 dni' },
              { value: 'all', label: 'Wszystko' },
            ]}
          />
          <p className="text-xs text-text-muted">Rozmiar: sm. Aktywny: {period}</p>
        </div>
      </Subsection>

      <Subsection n={3} title="Full width + 2 opcje">
        <div className="rounded-xl border border-border bg-surface-raised/40 p-5 max-w-md">
          <SegmentedTabs
            value={view}
            onChange={setView}
            fullWidth
            options={[
              { value: 'list', label: 'Lista' },
              { value: 'grid', label: 'Siatka' },
            ]}
          />
        </div>
      </Subsection>
    </div>
  );
}

